import React, { useState, useEffect, useMemo } from 'react';
import { ButtonTypes } from '../../../../../../../../Interfaces/componentTypes';
import Button from '../../../../../../../../Components/Button/button';
import { useTheme } from '../../../../../../../../Context/ThemeContext';
import { useGeneralHooks } from '../../../../../../../../Hooks/hooks';
import PreviewScreen from './PreviewScreen/previewScreen';
import styles from './templateEditor.module.scss';
import useDialogHooks from '../../../../../../../../Components/Dialog/dialog-hooks';
import CustomDialog from '../../../../../../../../Components/Dialog/customDialog';
import StyleVerificationDialog from '../../../../Dialogs/StyleVerification/styleVerificationDialog';

interface TemplateColors {
    light: Record<string, string>;
    dark: Record<string, string>;
}

interface IProps {
    currentTemplate: string;
    onSaveTemplate: (templateJson: string) => void;
    updateLoading: boolean;
}

const TemplateEditor: React.FC<IProps> = ({
    currentTemplate,
    onSaveTemplate,
    updateLoading
}) => {
    const { getTemplate } = useTheme();
    const { t } = useGeneralHooks();
    const defaultTemplate = useMemo(() => getTemplate("default") || { light: {}, dark: {} }, [getTemplate]);
    const { open, onOpen, onClose } = useDialogHooks();
    const [templateStyles, setTemplateStyles] = useState<TemplateColors>({
        light: { ...defaultTemplate.light },
        dark: { ...defaultTemplate.dark }
    });

    // Track which fields are in gradient mode
    const [gradientMode, setGradientMode] = useState<{ [key: string]: boolean }>({});

    // Track which font dropdowns are open
    const [openFontDropdowns, setOpenFontDropdowns] = useState<{ [key: string]: boolean }>({});

    // Track color input mode for each field (hex, rgb, rgba)
    const [colorInputMode, setColorInputMode] = useState<{ [key: string]: 'hex' | 'rgb' | 'rgba' }>({});

    // Validation dialog state
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [errorFields, setErrorFields] = useState<{ key: string, theme: 'light' | 'dark' }[]>([]);

    // Style fields configuration - organized by priority and grouped logically (includes colors and fonts)
    const styleFields = [
        // === BRANDING & IDENTITY ===
        { key: '--title', label: 'Business Name Color', description: 'Color for your business name/title in headers - the main brand identifier', supportsGradient: false },
        { key: '--title-font', label: 'Business Name Font', description: 'Font family for your business name/title - choose a font that represents your brand', supportsGradient: false, isFont: true },

        // === PRIMARY COLORS ===
        { key: '--primary-button', label: 'Primary Brand Color', description: 'Main brand color for buttons, highlights, and key actions - supports gradients', supportsGradient: true },
        { key: '--primary-hover', label: 'Brand Hover Color', description: 'Darker shade for hover states and active elements - creates depth and interaction feedback', supportsGradient: false },

        // === BACKGROUNDS ===
        { key: '--background-color', label: 'Page Background', description: 'Main page background color - the foundation of your design - supports gradients', supportsGradient: true },
        { key: '--card-bg', label: 'Card Background', description: 'Background for menu cards and content containers - supports gradients for modern look', supportsGradient: true },
        { key: '--footer-bg', label: 'Footer Background', description: 'Footer background with transparency - supports gradients and rgba values', supportsGradient: true },

        // === TEXT COLORS ===
        { key: '--text-color', label: 'Main Text Color', description: 'Primary text color for menu items, descriptions, and general content', supportsGradient: false },
        { key: '--text-dark', label: 'Emphasis Text Color', description: 'Darker text for prices, important information, and emphasis', supportsGradient: false },
        { key: '--input-label', label: 'Secondary Text Color', description: 'Color for labels, navigation text, footer icons, and secondary information', supportsGradient: false },

        // === INTERACTIVE ELEMENTS ===
        { key: '--input-bg', label: 'Input Field Background', description: 'Background for input fields, buttons, and interactive elements - supports gradients', supportsGradient: true },
        { key: '--disable-button', label: 'Disabled Element Color', description: 'Color for disabled buttons and inactive elements - indicates unavailable actions', supportsGradient: false },
        { key: '--icon-color', label: 'Icon Color', description: 'Color for navigation icons, action icons, and decorative symbols throughout the app', supportsGradient: false },

        // === SHADOWS & DEPTH ===
        { key: '--shadow-color', label: 'Shadow Color', description: 'Base color for shadows and glows - creates depth and visual hierarchy', supportsGradient: false },
        { key: '--box-shadow', label: 'Card Shadow', description: 'Shadow for cards and containers - defines elevation and separation', supportsGradient: false },
        { key: '--box-shadow-inset', label: 'Inner Shadow', description: 'Inner shadows for pressed states and depth effects in interactive elements', supportsGradient: false }
    ];

    // Font family options
    const fontOptions = [
        { value: 'Montserrat arm', label: 'Montserrat (Armenian)' },
        { value: 'Arial', label: 'Arial' },
        { value: 'Helvetica', label: 'Helvetica' },
        { value: 'Times New Roman', label: 'Times New Roman' },
        { value: 'Georgia', label: 'Georgia' },
        { value: 'Verdana', label: 'Verdana' },
        { value: 'Tahoma', label: 'Tahoma' },
        { value: 'Trebuchet MS', label: 'Trebuchet MS' },
        { value: 'Impact', label: 'Impact' },
        { value: 'Comic Sans MS', label: 'Comic Sans MS' },
        { value: 'Courier New', label: 'Courier New' },
        { value: 'Palatino', label: 'Palatino' },
        { value: 'Garamond', label: 'Garamond' },
        { value: 'Bookman', label: 'Bookman' },
        { value: 'Avant Garde', label: 'Avant Garde' },
        { value: 'Futura', label: 'Futura' },
        { value: 'Gill Sans', label: 'Gill Sans' },
        { value: 'Lucida Grande', label: 'Lucida Grande' },
        { value: 'Optima', label: 'Optima' },
        { value: 'serif', label: 'Serif (Generic)' },
        { value: 'sans-serif', label: 'Sans-serif (Generic)' },
        { value: 'monospace', label: 'Monospace (Generic)' },
        { value: 'cursive', label: 'Cursive (Generic)' },
        { value: 'fantasy', label: 'Fantasy (Generic)' }
    ];

    // Initialize template colors based on current template
    useEffect(() => {
        try {
            // Try to parse current template as JSON
            const parsedTemplate = JSON.parse(currentTemplate);
            if (parsedTemplate && parsedTemplate.light && parsedTemplate.dark) {
                setTemplateStyles({
                    light: {
                        ...defaultTemplate.light, // Ensure all default fields are present
                        ...parsedTemplate.light
                    },
                    dark: {
                        ...defaultTemplate.dark, // Ensure all default fields are present
                        ...parsedTemplate.dark
                    }
                });
            } else {
                setTemplateStyles({
                    light: { ...defaultTemplate.light },
                    dark: { ...defaultTemplate.dark }
                });
            }
        } catch (error) {
            // If not JSON, use default template
            setTemplateStyles({
                light: { ...defaultTemplate.light },
                dark: { ...defaultTemplate.dark }
            });
        }
    }, [currentTemplate, defaultTemplate]);

    const getColorValue = (value: string): string => {
        if (value.startsWith('#')) return value;

        if (value.includes('linear-gradient')) {
            const colorMatch = value.match(/#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})/);
            if (colorMatch) {
                return `#${colorMatch[1]}`;
            }
            const rgbMatch = value.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
            if (rgbMatch) {
                const r = parseInt(rgbMatch[1]);
                const g = parseInt(rgbMatch[2]);
                const b = parseInt(rgbMatch[3]);
                return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
            }
        }

        if (value.startsWith('rgb')) {
            const rgbMatch = value.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
            if (rgbMatch) {
                const r = parseInt(rgbMatch[1]);
                const g = parseInt(rgbMatch[2]);
                const b = parseInt(rgbMatch[3]);
                return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
            }
        }
        if (value.startsWith('rgba')) {
            const rgbaMatch = value.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/);
            if (rgbaMatch) {
                const r = parseInt(rgbaMatch[1]);
                const g = parseInt(rgbaMatch[2]);
                const b = parseInt(rgbaMatch[3]);
                return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
            }
        }
        return '#000000';
    };

    // Helper function to get the display value for the color picker
    const getColorPickerValue = (value: string): string => {
        return getColorValue(value);
    };

    // Helper functions for RGB/RGBA conversion
    const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
        const result = /^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/.exec(hex);
        if (!result) return null;
        
        let r, g, b;
        if (result[1].length === 3) {
            r = parseInt(result[1][0] + result[1][0], 16);
            g = parseInt(result[1][1] + result[1][1], 16);
            b = parseInt(result[1][2] + result[1][2], 16);
        } else {
            r = parseInt(result[1].substring(0, 2), 16);
            g = parseInt(result[1].substring(2, 4), 16);
            b = parseInt(result[1].substring(4, 6), 16);
        }
        return { r, g, b };
    };

    const rgbToHex = (r: number, g: number, b: number): string => {
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    const parseRgbValue = (value: string): { r: number; g: number; b: number; a?: number } | null => {
        const rgbMatch = value.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (rgbMatch) {
            return {
                r: parseInt(rgbMatch[1]),
                g: parseInt(rgbMatch[2]),
                b: parseInt(rgbMatch[3])
            };
        }
        
        const rgbaMatch = value.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
        if (rgbaMatch) {
            return {
                r: parseInt(rgbaMatch[1]),
                g: parseInt(rgbaMatch[2]),
                b: parseInt(rgbaMatch[3]),
                a: parseFloat(rgbaMatch[4])
            };
        }
        
        return null;
    };

    const getCurrentColorMode = (key: string, value: string): 'hex' | 'rgb' | 'rgba' => {
        if (value.startsWith('rgba')) return 'rgba';
        if (value.startsWith('rgb')) return 'rgb';
        return 'hex';
    };

    const toggleColorInputMode = (key: string) => {
        const currentMode = colorInputMode[key] || 'hex';
        const currentValue = templateStyles.light[key] || templateStyles.dark[key] || '#000000';
        
        let newMode: 'hex' | 'rgb' | 'rgba';
        if (currentMode === 'hex') {
            newMode = 'rgb';
        } else if (currentMode === 'rgb') {
            newMode = 'rgba';
        } else {
            newMode = 'hex';
        }
        
        setColorInputMode(prev => ({ ...prev, [key]: newMode }));
        
        // Convert current value to new format
        if (newMode === 'hex') {
            const rgb = parseRgbValue(currentValue);
            if (rgb) {
                const hexValue = rgbToHex(rgb.r, rgb.g, rgb.b);
                setTemplateStyles(prev => ({
                    light: { ...prev.light, [key]: hexValue },
                    dark: { ...prev.dark, [key]: hexValue }
                }));
            }
        } else if (newMode === 'rgb') {
            const hex = getColorValue(currentValue);
            const rgb = hexToRgb(hex);
            if (rgb) {
                const rgbValue = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
                setTemplateStyles(prev => ({
                    light: { ...prev.light, [key]: rgbValue },
                    dark: { ...prev.dark, [key]: rgbValue }
                }));
            }
        } else if (newMode === 'rgba') {
            const hex = getColorValue(currentValue);
            const rgb = hexToRgb(hex);
            if (rgb) {
                const rgbaValue = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`;
                setTemplateStyles(prev => ({
                    light: { ...prev.light, [key]: rgbaValue },
                    dark: { ...prev.dark, [key]: rgbaValue }
                }));
            }
        }
    };

    const handleColorChange = (theme: 'light' | 'dark', key: string, value: string) => {
        // If this is a color picker change (hex value), convert to current mode
        if (value.startsWith('#') && !value.includes('linear-gradient')) {
            const currentMode = colorInputMode[key] || 'hex';
            
            if (currentMode === 'rgb') {
                const rgb = hexToRgb(value);
                if (rgb) {
                    const rgbValue = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
                    setTemplateStyles(prev => ({
                        ...prev,
                        [theme]: {
                            ...prev[theme],
                            [key]: rgbValue
                        }
                    }));
                    return;
                }
            } else if (currentMode === 'rgba') {
                const rgb = hexToRgb(value);
                if (rgb) {
                    const rgbaValue = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`;
                    setTemplateStyles(prev => ({
                        ...prev,
                        [theme]: {
                            ...prev[theme],
                            [key]: rgbaValue
                        }
                    }));
                    return;
                }
            }
        }
        
        // For other cases (text input, gradient, etc.), use the value as-is
        setTemplateStyles(prev => ({
            ...prev,
            [theme]: {
                ...prev[theme],
                [key]: value
            }
        }));
    };

    const toggleGradientMode = (key: string) => {
        setGradientMode(prev => ({
            ...prev,
            [key]: !prev[key]
        }));

        if (!gradientMode[key]) {
            const currentColor = templateStyles.light[key] || templateStyles.dark[key] || '#ffffff';
            const gradientValue = `linear-gradient(135deg, ${currentColor}, ${currentColor})`;

            setTemplateStyles(prev => ({
                light: { ...prev.light, [key]: gradientValue },
                dark: { ...prev.dark, [key]: gradientValue }
            }));
        } else {
            // If switching to solid mode, extract color from gradient
            const currentValue = templateStyles.light[key] || templateStyles.dark[key] || '#ffffff';
            const solidColor = getColorValue(currentValue);

            setTemplateStyles(prev => ({
                light: { ...prev.light, [key]: solidColor },
                dark: { ...prev.dark, [key]: solidColor }
            }));
        }
    };

    const isGradientMode = (key: string): boolean => {
        return Boolean(gradientMode[key]) || Boolean(templateStyles.light[key] && templateStyles.light[key].includes('linear-gradient'));
    };

    const toggleFontDropdown = (key: string) => {
        setOpenFontDropdowns(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const selectFont = (key: string, fontValue: string, theme: 'light' | 'dark') => {
        setTemplateStyles(prev => {
            const newColors = {
                ...prev,
                [theme]: { ...prev[theme], [key]: fontValue }
            };

            // Apply font directly to document for immediate effect
            document.documentElement.style.setProperty(key, fontValue);

            return newColors;
        });
        setOpenFontDropdowns(prev => ({
            ...prev,
            [key]: false
        }));
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.fontDropdownContainer')) {
                setOpenFontDropdowns({});
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    // Validation functions
    const isValidColor = (value: string): boolean => {
        if (!value || value.trim() === '') return false;

        // Check for valid hex colors
        if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value)) return true;

        // Check for valid rgb/rgba colors
        if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/.test(value)) return true;

        // Check for valid linear gradients
        if (value.includes('linear-gradient')) {
            // Basic gradient validation - should contain color values
            const hasColors = /#[A-Fa-f0-9]{6}|#[A-Fa-f0-9]{3}|rgb\(|rgba\(/.test(value);
            return hasColors;
        }

        return false;
    };

    const isValidCSSValue = (value: string): boolean => {
        if (!value || value.trim() === '') return false;

        // Check for valid hex colors
        if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value)) return true;

        // Check for valid rgb/rgba colors
        if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/.test(value)) return true;

        // Check for valid linear gradients
        if (value.includes('linear-gradient')) {
            const hasColors = /#[A-Fa-f0-9]{6}|#[A-Fa-f0-9]{3}|rgb\(|rgba\(/.test(value);
            return hasColors;
        }

        // Check for CSS shadow values (box-shadow, text-shadow, etc.)
        if (value.includes('px') && value.includes('rgba')) {
            // Basic shadow validation - should contain px values and rgba colors
            const hasPx = /\d+px/.test(value);
            const hasRgba = /rgba?\(/.test(value);
            return hasPx && hasRgba;
        }

        // Check for CSS inset shadows
        if (value.includes('inset') && value.includes('px')) {
            return true;
        }

        return false;
    };

    const isValidFont = (value: string): boolean => {
        if (!value || value.trim() === '') return false;

        // Check if it's a valid font family name
        const validFonts = fontOptions.map(font => font.value);
        return validFonts.includes(value) ||
            ['serif', 'sans-serif', 'monospace', 'cursive', 'fantasy'].includes(value);
    };

    const validateTemplate = (template: TemplateColors): { isValid: boolean; errors: string[]; validatedTemplate: TemplateColors; errorFields: { key: string, theme: 'light' | 'dark' }[] } => {
        const errors: string[] = [];
        const errorFields: { key: string, theme: 'light' | 'dark' }[] = [];
        const validatedTemplate: TemplateColors = {
            light: {},
            dark: {}
        };

        // Get default template for fallback
        const defaultTemplate = getTemplate("default") || { light: {}, dark: {} };

        // Validate each field
        styleFields.forEach(field => {
            const lightValue = template.light[field.key];
            const darkValue = template.dark[field.key];

            // Check if field is required
            if (!lightValue || !darkValue) {
                // Create specific error messages for each theme
                if (!lightValue && !darkValue) {
                    errors.push(`Missing values for ${field.label} in both themes`);
                } else if (!lightValue) {
                    errors.push(`Missing value for ${field.label} in light mode`);
                } else if (!darkValue) {
                    errors.push(`Missing value for ${field.label} in dark mode`);
                }

                // Track which themes have errors
                if (!lightValue) errorFields.push({ key: field.key, theme: 'light' });
                if (!darkValue) errorFields.push({ key: field.key, theme: 'dark' });
                // Use default values
                validatedTemplate.light[field.key] = defaultTemplate.light[field.key] || '';
                validatedTemplate.dark[field.key] = defaultTemplate.dark[field.key] || '';
                return;
            }

            // Validate based on field type
            if (field.isFont) {
                const lightValid = isValidFont(lightValue);
                const darkValid = isValidFont(darkValue);

                if (!lightValid || !darkValid) {
                    // Create specific error messages for each theme
                    if (!lightValid && !darkValid) {
                        errors.push(`Invalid font for ${field.label} in both themes`);
                    } else if (!lightValid) {
                        errors.push(`Invalid font for ${field.label} in light mode`);
                    } else if (!darkValid) {
                        errors.push(`Invalid font for ${field.label} in dark mode`);
                    }

                    // Track which themes have errors
                    if (!lightValid) errorFields.push({ key: field.key, theme: 'light' });
                    if (!darkValid) errorFields.push({ key: field.key, theme: 'dark' });
                    validatedTemplate.light[field.key] = defaultTemplate.light[field.key] || 'Montserrat arm';
                    validatedTemplate.dark[field.key] = defaultTemplate.dark[field.key] || 'Montserrat arm';
                } else {
                    validatedTemplate.light[field.key] = lightValue;
                    validatedTemplate.dark[field.key] = darkValue;
                }
            } else {
                // Use CSS value validation for shadow fields, color validation for others
                const isShadowField = field.key.includes('shadow') || field.key.includes('box-shadow');
                const validationFunction = isShadowField ? isValidCSSValue : isValidColor;

                const lightValid = validationFunction(lightValue);
                const darkValid = validationFunction(darkValue);

                if (!lightValid || !darkValid) {
                    // Create specific error messages for each theme
                    const fieldType = isShadowField ? 'CSS value' : 'color';
                    if (!lightValid && !darkValid) {
                        errors.push(`Invalid ${fieldType} for ${field.label} in both themes`);
                    } else if (!lightValid) {
                        errors.push(`Invalid ${fieldType} for ${field.label} in light mode`);
                    } else if (!darkValid) {
                        errors.push(`Invalid ${fieldType} for ${field.label} in dark mode`);
                    }

                    // Track which themes have errors
                    if (!lightValid) errorFields.push({ key: field.key, theme: 'light' });
                    if (!darkValid) errorFields.push({ key: field.key, theme: 'dark' });
                    validatedTemplate.light[field.key] = defaultTemplate.light[field.key] || (isShadowField ? '0px 4px 8px rgba(0, 0, 0, 0.3)' : '#000000');
                    validatedTemplate.dark[field.key] = defaultTemplate.dark[field.key] || (isShadowField ? '0px 4px 8px rgba(0, 0, 0, 0.3)' : '#000000');
                } else {
                    validatedTemplate.light[field.key] = lightValue;
                    validatedTemplate.dark[field.key] = darkValue;
                }
            }
        });

        return {
            isValid: errors.length === 0,
            errors,
            validatedTemplate,
            errorFields
        };
    };

    const handleSave = () => {
        // Validate template before saving
        const validation = validateTemplate(templateStyles);

        if (!validation.isValid) {
            // Show validation dialog instead of alert
            setValidationErrors(validation.errors);
            setErrorFields(validation.errorFields);
            onOpen();
        } else {
            // All valid, save as is
            const templateJson = JSON.stringify(templateStyles);
            onSaveTemplate(templateJson);
        }
    };

    const handleFixErrors = () => {
        onClose();
        // Scroll to first error field
        if (errorFields.length > 0) {
            const firstError = errorFields[0];
            // Use the field key and theme to find the specific element
            const element = document.querySelector(`[data-field-key="${firstError.key}"][data-theme="${firstError.theme}"]`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };

    const handleSkipErrors = () => {
        onClose();
        // Apply validated template with default values for errors
        const validation = validateTemplate(templateStyles);
        setTemplateStyles(validation.validatedTemplate);

        // Save the validated template
        const templateJson = JSON.stringify(validation.validatedTemplate);
        onSaveTemplate(templateJson);
    };

    const handleReset = () => {
        const currentDefaultTemplate = getTemplate("default") || { light: {}, dark: {} };
        setTemplateStyles({
            light: { ...currentDefaultTemplate.light },
            dark: { ...currentDefaultTemplate.dark }
        });
        // Reset without saving - user can save manually if they want
    };

    const handleSwapModes = () => {
        setTemplateStyles(prev => ({
            light: { ...prev.dark },
            dark: { ...prev.light }
        }));
    };


    return (
        <div className={styles.container}>
            <div className={styles.mainContent}>
                <div className={styles.editorSection}>
                    <div className={styles.header}>
                        <h2 className={styles.title}>{t("My_Business.Templates.Editor.Title")}</h2>
                        <p className={styles.subtitle}>{t("My_Business.Templates.Editor.Subtitle")}</p>
                    </div>

                    <div className={styles.swapButtonContainer}>
                        <Button
                            title={t("My_Business.Templates.Editor.SwapModes", "Swap Modes")}
                            buttonType={ButtonTypes.Secondary}
                            onClick={handleSwapModes}
                            buttonStyle={styles.swapButton}
                            buttonTitleStyle={styles.swapButtonTitle}
                        />
                    </div>

                    <div className={styles.content}>
                        <div className={styles.themeSection} data-section="light-mode">
                            <h3 className={styles.sectionTitle}>Light Mode</h3>
                            <div className={styles.colorGrid}>
                                {styleFields.map(field => (
                                    <div key={`light-${field.key}`} className={styles.colorField} data-field-key={field.key}>
                                        <label className={styles.label}>
                                            {field.label}
                                            <span className={styles.description}>{field.description}</span>
                                        </label>
                                        <div className={styles.inputContainer}>
                                            {field.isFont ? (
                                                <div className={styles.fontDropdownContainer}>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            toggleFontDropdown(field.key);
                                                        }}
                                                        className={styles.fontSelectButton}
                                                    >
                                                        <span style={{ fontFamily: templateStyles.light[field.key] || 'Montserrat arm' }}>
                                                            {(() => {
                                                                const currentValue = templateStyles.light[field.key] || 'Montserrat arm';
                                                                const foundFont = fontOptions.find(font => font.value === currentValue);
                                                                return foundFont?.label || 'Montserrat (Armenian)';
                                                            })()}
                                                        </span>
                                                        <span className={`${styles.dropdownArrow} ${openFontDropdowns[field.key] ? styles.open : ''}`}>
                                                            {openFontDropdowns[field.key] ? '▲' : '▼'}
                                                        </span>
                                                    </button>
                                                    {openFontDropdowns[field.key] && (
                                                        <div className={styles.fontDropdown}>
                                                            {fontOptions.map(font => (
                                                                <div
                                                                    key={font.value}
                                                                    className={styles.fontOption}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        selectFont(field.key, font.value, 'light');
                                                                    }}
                                                                    style={{ fontFamily: font.value }}
                                                                >
                                                                    {font.label}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <>
                                                    <div className={styles.colorInputRow}>
                                                        <input
                                                            type="color"
                                                            value={getColorPickerValue(templateStyles.light[field.key] || '#000000')}
                                                            onChange={(e) => handleColorChange('light', field.key, e.target.value)}
                                                            className={`${styles.colorInput} ${errorFields.some(err => err.key === field.key && err.theme === 'light') ? styles.errorField : ''}`}
                                                            disabled={field.supportsGradient ? isGradientMode(field.key) : false}
                                                            data-field-key={field.key}
                                                            data-theme="light"
                                                            style={{
                                                                border: errorFields.some(err => err.key === field.key && err.theme === 'light') ? '2px solid #dc3545' : undefined,
                                                                boxShadow: errorFields.some(err => err.key === field.key && err.theme === 'light') ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                                                            }}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleColorInputMode(field.key)}
                                                            className={styles.colorModeToggle}
                                                            title={`Current: ${(colorInputMode[field.key] || 'hex').toUpperCase()}. Click to switch mode.`}
                                                        >
                                                            {(colorInputMode[field.key] || 'hex').toUpperCase()}
                                                        </button>
                                                        {field.supportsGradient && (
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleGradientMode(field.key)}
                                                                className={`${styles.gradientToggle} ${isGradientMode(field.key) ? styles.active : ''}`}
                                                                title={isGradientMode(field.key) ? 'Switch to solid color' : 'Switch to gradient'}
                                                            >
                                                                {isGradientMode(field.key) ? '🎨' : '🔲'}
                                                            </button>
                                                        )}
                                                    </div>
                                                    {(() => {
                                                        const currentMode = colorInputMode[field.key] || 'hex';
                                                        const currentValue = templateStyles.light[field.key] || '';
                                                        const parsedRgb = parseRgbValue(currentValue);
                                                        
                                                        if (currentMode === 'rgb' && parsedRgb) {
                                                            return (
                                                                <div className={styles.rgbInputs}>
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max="255"
                                                                        value={parsedRgb.r}
                                                                        onChange={(e) => {
                                                                            const newR = parseInt(e.target.value) || 0;
                                                                            const newValue = `rgb(${newR}, ${parsedRgb.g}, ${parsedRgb.b})`;
                                                                            handleColorChange('light', field.key, newValue);
                                                                        }}
                                                                        className={`${styles.rgbInput} ${errorFields.some(err => err.key === field.key && err.theme === 'light') ? styles.errorField : ''}`}
                                                                        placeholder="R"
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max="255"
                                                                        value={parsedRgb.g}
                                                                        onChange={(e) => {
                                                                            const newG = parseInt(e.target.value) || 0;
                                                                            const newValue = `rgb(${parsedRgb.r}, ${newG}, ${parsedRgb.b})`;
                                                                            handleColorChange('light', field.key, newValue);
                                                                        }}
                                                                        className={`${styles.rgbInput} ${errorFields.some(err => err.key === field.key && err.theme === 'light') ? styles.errorField : ''}`}
                                                                        placeholder="G"
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max="255"
                                                                        value={parsedRgb.b}
                                                                        onChange={(e) => {
                                                                            const newB = parseInt(e.target.value) || 0;
                                                                            const newValue = `rgb(${parsedRgb.r}, ${parsedRgb.g}, ${newB})`;
                                                                            handleColorChange('light', field.key, newValue);
                                                                        }}
                                                                        className={`${styles.rgbInput} ${errorFields.some(err => err.key === field.key && err.theme === 'light') ? styles.errorField : ''}`}
                                                                        placeholder="B"
                                                                    />
                                                                </div>
                                                            );
                                                        } else if (currentMode === 'rgba' && parsedRgb) {
                                                            return (
                                                                <div className={styles.rgbaInputs}>
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max="255"
                                                                        value={parsedRgb.r}
                                                                        onChange={(e) => {
                                                                            const newR = parseInt(e.target.value) || 0;
                                                                            const alpha = parsedRgb.a || 1;
                                                                            const newValue = `rgba(${newR}, ${parsedRgb.g}, ${parsedRgb.b}, ${alpha})`;
                                                                            handleColorChange('light', field.key, newValue);
                                                                        }}
                                                                        className={`${styles.rgbaInput} ${errorFields.some(err => err.key === field.key && err.theme === 'light') ? styles.errorField : ''}`}
                                                                        placeholder="R"
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max="255"
                                                                        value={parsedRgb.g}
                                                                        onChange={(e) => {
                                                                            const newG = parseInt(e.target.value) || 0;
                                                                            const alpha = parsedRgb.a || 1;
                                                                            const newValue = `rgba(${parsedRgb.r}, ${newG}, ${parsedRgb.b}, ${alpha})`;
                                                                            handleColorChange('light', field.key, newValue);
                                                                        }}
                                                                        className={`${styles.rgbaInput} ${errorFields.some(err => err.key === field.key && err.theme === 'light') ? styles.errorField : ''}`}
                                                                        placeholder="G"
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max="255"
                                                                        value={parsedRgb.b}
                                                                        onChange={(e) => {
                                                                            const newB = parseInt(e.target.value) || 0;
                                                                            const alpha = parsedRgb.a || 1;
                                                                            const newValue = `rgba(${parsedRgb.r}, ${parsedRgb.g}, ${newB}, ${alpha})`;
                                                                            handleColorChange('light', field.key, newValue);
                                                                        }}
                                                                        className={`${styles.rgbaInput} ${errorFields.some(err => err.key === field.key && err.theme === 'light') ? styles.errorField : ''}`}
                                                                        placeholder="B"
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max="1"
                                                                        step="0.1"
                                                                        value={parsedRgb.a || 1}
                                                                        onChange={(e) => {
                                                                            const newA = parseFloat(e.target.value) || 1;
                                                                            const newValue = `rgba(${parsedRgb.r}, ${parsedRgb.g}, ${parsedRgb.b}, ${newA})`;
                                                                            handleColorChange('light', field.key, newValue);
                                                                        }}
                                                                        className={`${styles.rgbaInput} ${errorFields.some(err => err.key === field.key && err.theme === 'light') ? styles.errorField : ''}`}
                                                                        placeholder="A"
                                                                    />
                                                                </div>
                                                            );
                                                        } else {
                                                            return (
                                                    <input
                                                        type="text"
                                                        value={templateStyles.light[field.key] || ''}
                                                        onChange={(e) => handleColorChange('light', field.key, e.target.value)}
                                                        placeholder={field.supportsGradient && isGradientMode(field.key) ? "linear-gradient(135deg, #ffffff, #f8f8f8)" : "Enter color value"}
                                                        className={`${styles.textInput} ${errorFields.some(err => err.key === field.key && err.theme === 'light') ? styles.errorField : ''}`}
                                                        data-field-key={field.key}
                                                        data-theme="light"
                                                        style={{
                                                            border: errorFields.some(err => err.key === field.key && err.theme === 'light') ? '2px solid #dc3545' : undefined,
                                                            boxShadow: errorFields.some(err => err.key === field.key && err.theme === 'light') ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                                                        }}
                                                    />
                                                            );
                                                        }
                                                    })()}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className={styles.themeSection} data-section="dark-mode">
                            <h3 className={styles.sectionTitle}>Dark Mode</h3>
                            <div className={styles.colorGrid}>
                                {styleFields.map(field => (
                                    <div key={`dark-${field.key}`} className={styles.colorField} data-field-key={field.key}>
                                        <label className={styles.label}>
                                            {field.label}
                                            <span className={styles.description}>{field.description}</span>
                                        </label>
                                        <div className={styles.inputContainer}>
                                            {field.isFont ? (
                                                <div className={styles.fontDropdownContainer}>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            toggleFontDropdown(field.key);
                                                        }}
                                                        className={styles.fontSelectButton}
                                                    >
                                                        <span style={{ fontFamily: templateStyles.dark[field.key] || 'Montserrat arm' }}>
                                                            {(() => {
                                                                const currentValue = templateStyles.dark[field.key] || 'Montserrat arm';
                                                                const foundFont = fontOptions.find(font => font.value === currentValue);
                                                                return foundFont?.label || 'Montserrat (Armenian)';
                                                            })()}
                                                        </span>
                                                        <span className={`${styles.dropdownArrow} ${openFontDropdowns[field.key] ? styles.open : ''}`}>
                                                            {openFontDropdowns[field.key] ? '▲' : '▼'}
                                                        </span>
                                                    </button>
                                                    {openFontDropdowns[field.key] && (
                                                        <div className={styles.fontDropdown}>
                                                            {fontOptions.map(font => (
                                                                <div
                                                                    key={font.value}
                                                                    className={styles.fontOption}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        selectFont(field.key, font.value, 'dark');
                                                                    }}
                                                                    style={{ fontFamily: font.value }}
                                                                >
                                                                    {font.label}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <>
                                                    <div className={styles.colorInputRow}>
                                                        <input
                                                            type="color"
                                                            value={getColorPickerValue(templateStyles.dark[field.key] || '#000000')}
                                                            onChange={(e) => handleColorChange('dark', field.key, e.target.value)}
                                                            className={`${styles.colorInput} ${errorFields.some(err => err.key === field.key && err.theme === 'dark') ? styles.errorField : ''}`}
                                                            disabled={field.supportsGradient ? isGradientMode(field.key) : false}
                                                            data-field-key={field.key}
                                                            data-theme="dark"
                                                            style={{
                                                                border: errorFields.some(err => err.key === field.key && err.theme === 'dark') ? '2px solid #dc3545' : undefined,
                                                                boxShadow: errorFields.some(err => err.key === field.key && err.theme === 'dark') ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                                                            }}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleColorInputMode(field.key)}
                                                            className={styles.colorModeToggle}
                                                            title={`Current: ${(colorInputMode[field.key] || 'hex').toUpperCase()}. Click to switch mode.`}
                                                        >
                                                            {(colorInputMode[field.key] || 'hex').toUpperCase()}
                                                        </button>
                                                        {field.supportsGradient && (
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleGradientMode(field.key)}
                                                                className={`${styles.gradientToggle} ${isGradientMode(field.key) ? styles.active : ''}`}
                                                                title={isGradientMode(field.key) ? 'Switch to solid color' : 'Switch to gradient'}
                                                            >
                                                                {isGradientMode(field.key) ? '🎨' : '🔲'}
                                                            </button>
                                                        )}
                                                    </div>
                                                    {(() => {
                                                        const currentMode = colorInputMode[field.key] || 'hex';
                                                        const currentValue = templateStyles.dark[field.key] || '';
                                                        const parsedRgb = parseRgbValue(currentValue);
                                                        
                                                        if (currentMode === 'rgb' && parsedRgb) {
                                                            return (
                                                                <div className={styles.rgbInputs}>
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max="255"
                                                                        value={parsedRgb.r}
                                                                        onChange={(e) => {
                                                                            const newR = parseInt(e.target.value) || 0;
                                                                            const newValue = `rgb(${newR}, ${parsedRgb.g}, ${parsedRgb.b})`;
                                                                            handleColorChange('dark', field.key, newValue);
                                                                        }}
                                                                        className={`${styles.rgbInput} ${errorFields.some(err => err.key === field.key && err.theme === 'dark') ? styles.errorField : ''}`}
                                                                        placeholder="R"
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max="255"
                                                                        value={parsedRgb.g}
                                                                        onChange={(e) => {
                                                                            const newG = parseInt(e.target.value) || 0;
                                                                            const newValue = `rgb(${parsedRgb.r}, ${newG}, ${parsedRgb.b})`;
                                                                            handleColorChange('dark', field.key, newValue);
                                                                        }}
                                                                        className={`${styles.rgbInput} ${errorFields.some(err => err.key === field.key && err.theme === 'dark') ? styles.errorField : ''}`}
                                                                        placeholder="G"
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max="255"
                                                                        value={parsedRgb.b}
                                                                        onChange={(e) => {
                                                                            const newB = parseInt(e.target.value) || 0;
                                                                            const newValue = `rgb(${parsedRgb.r}, ${parsedRgb.g}, ${newB})`;
                                                                            handleColorChange('dark', field.key, newValue);
                                                                        }}
                                                                        className={`${styles.rgbInput} ${errorFields.some(err => err.key === field.key && err.theme === 'dark') ? styles.errorField : ''}`}
                                                                        placeholder="B"
                                                                    />
                                                                </div>
                                                            );
                                                        } else if (currentMode === 'rgba' && parsedRgb) {
                                                            return (
                                                                <div className={styles.rgbaInputs}>
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max="255"
                                                                        value={parsedRgb.r}
                                                                        onChange={(e) => {
                                                                            const newR = parseInt(e.target.value) || 0;
                                                                            const alpha = parsedRgb.a || 1;
                                                                            const newValue = `rgba(${newR}, ${parsedRgb.g}, ${parsedRgb.b}, ${alpha})`;
                                                                            handleColorChange('dark', field.key, newValue);
                                                                        }}
                                                                        className={`${styles.rgbaInput} ${errorFields.some(err => err.key === field.key && err.theme === 'dark') ? styles.errorField : ''}`}
                                                                        placeholder="R"
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max="255"
                                                                        value={parsedRgb.g}
                                                                        onChange={(e) => {
                                                                            const newG = parseInt(e.target.value) || 0;
                                                                            const alpha = parsedRgb.a || 1;
                                                                            const newValue = `rgba(${parsedRgb.r}, ${newG}, ${parsedRgb.b}, ${alpha})`;
                                                                            handleColorChange('dark', field.key, newValue);
                                                                        }}
                                                                        className={`${styles.rgbaInput} ${errorFields.some(err => err.key === field.key && err.theme === 'dark') ? styles.errorField : ''}`}
                                                                        placeholder="G"
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max="255"
                                                                        value={parsedRgb.b}
                                                                        onChange={(e) => {
                                                                            const newB = parseInt(e.target.value) || 0;
                                                                            const alpha = parsedRgb.a || 1;
                                                                            const newValue = `rgba(${parsedRgb.r}, ${parsedRgb.g}, ${newB}, ${alpha})`;
                                                                            handleColorChange('dark', field.key, newValue);
                                                                        }}
                                                                        className={`${styles.rgbaInput} ${errorFields.some(err => err.key === field.key && err.theme === 'dark') ? styles.errorField : ''}`}
                                                                        placeholder="B"
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max="1"
                                                                        step="0.1"
                                                                        value={parsedRgb.a || 1}
                                                                        onChange={(e) => {
                                                                            const newA = parseFloat(e.target.value) || 1;
                                                                            const newValue = `rgba(${parsedRgb.r}, ${parsedRgb.g}, ${parsedRgb.b}, ${newA})`;
                                                                            handleColorChange('dark', field.key, newValue);
                                                                        }}
                                                                        className={`${styles.rgbaInput} ${errorFields.some(err => err.key === field.key && err.theme === 'dark') ? styles.errorField : ''}`}
                                                                        placeholder="A"
                                                                    />
                                                                </div>
                                                            );
                                                        } else {
                                                            return (
                                                    <input
                                                        type="text"
                                                        value={templateStyles.dark[field.key] || ''}
                                                        onChange={(e) => handleColorChange('dark', field.key, e.target.value)}
                                                        placeholder={field.supportsGradient && isGradientMode(field.key) ? "linear-gradient(135deg, #000000, #1f1f1f)" : "Enter color value"}
                                                        className={`${styles.textInput} ${errorFields.some(err => err.key === field.key && err.theme === 'dark') ? styles.errorField : ''}`}
                                                        data-field-key={field.key}
                                                        data-theme="dark"
                                                        style={{
                                                            border: errorFields.some(err => err.key === field.key && err.theme === 'dark') ? '2px solid #dc3545' : undefined,
                                                            boxShadow: errorFields.some(err => err.key === field.key && err.theme === 'dark') ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                                                        }}
                                                    />
                                                            );
                                                        }
                                                    })()}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className={styles.actions}>
                        <Button
                            title={t("My_Business.Templates.Editor.Reset")}
                            buttonType={ButtonTypes.Secondary}
                            onClick={handleReset}
                            buttonStyle={styles.resetButton}
                            buttonTitleStyle={styles.resetButtonTitle}
                        />
                        <Button
                            title={t("My_Business.Templates.Editor.Save")}
                            buttonType={ButtonTypes.Primery}
                            onClick={handleSave}
                            isLoading={updateLoading}
                            buttonStyle={styles.saveButton}
                        />
                    </div>
                </div>

                <div className={styles.previewSection}>
                    <PreviewScreen templateColors={templateStyles} />
                </div>
            </div>

            {/* Validation Dialog */}
            <CustomDialog
                open={open}
                onClose={onClose}
                children={
                    <StyleVerificationDialog
                        onClose={onClose}
                        onFix={handleFixErrors}
                        onSkip={handleSkipErrors}
                        errors={validationErrors}
                    />
                }
            />
        </div>
    );
};

export default TemplateEditor;
